import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { refreshToken as apiRefreshToken } from '../api/auth'
import { persistStorage } from './storage'

// Single-flight refresh: N concurrent 401s each calling refreshToken() must
// share one API call — otherwise every retry races with the others' refresh
// and last-set-wins clobbers the token the others already retried with. Kept
// module-level (not store state) so the persist middleware never serializes
// a Promise.
let refreshInFlight: Promise<void> | null = null

interface AuthState {
  token: string | null
  userId: string | null
  username: string | null
  isSystemAdmin: boolean
  setAuth: (
    token: string,
    userId: string,
    username: string,
    isSystemAdmin: boolean,
  ) => void
  logout: () => void
  refreshToken: () => Promise<void>
}

/** Derived selector: authenticated iff a token is held. */
export const selectIsAuthenticated = (s: AuthState) => !!s.token

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      userId: null,
      username: null,
      isSystemAdmin: false,
      setAuth: (token, userId, username, isSystemAdmin) =>
        set({ token, userId, username, isSystemAdmin }),
      logout: () => {
        set({ token: null, userId: null, username: null, isSystemAdmin: false })
        window.location.href = '/login'
      },
      refreshToken: () => {
        if (!refreshInFlight) {
          refreshInFlight = (async () => {
            const { token } = get()
            if (!token) return
            const res = await apiRefreshToken()
            set({
              token: res.token,
              userId: res.user_id,
              username: res.username,
              isSystemAdmin: res.is_system_admin,
            })
          })().finally(() => {
            refreshInFlight = null
          })
        }
        return refreshInFlight
      },
    }),
    {
      name: 'chromaframe-auth',
      version: 1,
      storage: persistStorage('chromaframe-auth'),
    },
  ),
)
